import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Plus, ShieldCheck } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  Empty,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  Textarea,
  useAuth,
  useCan,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import type { Role } from '../../lib/rbac-types'

export const ROLES_KEY = ['admin', 'roles'] as const

export function useRoles() {
  const { session } = useAuth()
  const allowed = useCan('roles.view')
  return useQuery({
    queryKey: ROLES_KEY,
    enabled: allowed,
    queryFn: () => session.api.get<{ data: Role[] }>('/admin/roles').then((r) => r.data),
  })
}

export function RoleBadges({ role }: { role: Role }) {
  const { t } = useTranslation()
  return (
    <span className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
      {role.isSystem && <Badge variant="indigo">{t('rbac.roles.system')}</Badge>}
      {role.requiresMfa && <Badge variant="sahel">{t('rbac.roles.mfa')}</Badge>}
      {!role.isActive && <Badge variant="neutral">{t('rbac.roles.inactive')}</Badge>}
    </span>
  )
}

/** Liste des profils (TODO 4.6). */
export function RolesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const roles = useRoles()
  const [creating, setCreating] = useState(false)

  const columns: Column<Role>[] = [
    {
      key: 'label',
      header: t('rbac.roles.label'),
      cell: (role) => (
        <div>
          <div style={{ fontWeight: 600 }}>{role.label}</div>
          <span className="perm-code">{role.code}</span>
        </div>
      ),
    },
    { key: 'badges', header: '', cell: (role) => <RoleBadges role={role} /> },
    {
      key: 'permissionsCount',
      header: t('rbac.roles.permissions'),
      numeric: true,
      cell: (role) => role.permissionsCount ?? 0,
    },
    {
      key: 'usersCount',
      header: t('rbac.roles.users'),
      numeric: true,
      cell: (role) => role.usersCount ?? 0,
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow={t('rbac.roles.eyebrow')}
        title={t('rbac.roles.title')}
        description={t('rbac.roles.description')}
        actions={
          <Can perm="roles.create">
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              {t('rbac.roles.new')}
            </Button>
          </Can>
        }
      />
      {roles.isError && <Alert tone="danger">{errorMessage(roles.error)}</Alert>}
      <Card>
        <Table
          columns={columns}
          rows={roles.data ?? []}
          rowKey={(role) => role.id}
          loading={roles.isPending}
          caption={t('rbac.roles.title')}
          onRowClick={(role) => navigate(`/profils/${role.id}`)}
          empty={<Empty icon={<ShieldCheck aria-hidden="true" />} title={t('rbac.roles.empty')} />}
        />
      </Card>
      <CreateRoleModal
        open={creating}
        onClose={() => setCreating(false)}
        roles={roles.data ?? []}
      />
    </>
  )
}

/** Création d'un profil, vide ou par copie d'un profil existant. */
export function CreateRoleModal({
  open,
  onClose,
  roles,
  sourceId,
}: {
  open: boolean
  onClose: () => void
  roles: Role[]
  sourceId?: string
}) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ code: '', label: '', description: '', from: sourceId ?? '' })

  const create = useMutation({
    mutationFn: () =>
      form.from
        ? session.api.post<Role>(`/admin/roles/${form.from}/duplicate`, {
            code: form.code,
            label: form.label,
          })
        : session.api.post<Role>('/admin/roles', {
            code: form.code,
            label: form.label,
            description: form.description || null,
            permissions: [],
          }),
    onSuccess: async (role) => {
      await queryClient.invalidateQueries({ queryKey: ROLES_KEY })
      onClose()
      setForm({ code: '', label: '', description: '', from: '' })
      navigate(`/profils/${role.id}`)
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(create.error)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('rbac.create.title')}
      description={t('rbac.create.description')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('rbac.create.cancel')}
          </Button>
          <Button variant="primary" type="submit" form="create-role" loading={create.isPending}>
            {t('rbac.create.submit')}
          </Button>
        </>
      }
    >
      <form id="create-role" className="stack" onSubmit={submit}>
        <Input
          label={t('rbac.create.label')}
          required
          value={form.label}
          error={errors.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <Input
          label={t('rbac.create.code')}
          hint={t('rbac.create.codeHint')}
          required
          mono
          pattern="[a-z][a-z0-9_]{2,59}"
          value={form.code}
          error={errors.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
        />
        <Select
          label={t('rbac.create.from')}
          value={form.from}
          onChange={(e) => setForm({ ...form, from: e.target.value })}
          options={[
            { value: '', label: t('rbac.create.fromNone') },
            ...roles
              .filter((role) => role.code !== 'super_admin')
              .map((role) => ({ value: role.id, label: role.label })),
          ]}
        />
        {!form.from && (
          <Textarea
            label={t('rbac.create.descriptionField')}
            rows={3}
            value={form.description}
            error={errors.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        )}
      </form>
    </Modal>
  )
}
